import React, { useEffect, useMemo, useState } from 'react';
// import { Outlet } from 'react-router-dom'; // NOTE: Project is using React Router v5, Outlet is not available.
import { useHistory, useLocation } from 'react-router-dom';
import CRMSidebar from './components/CRMSidebar';
import CommandPalette from './components/CommandPalette';
import QuickLogDrawer from './components/QuickLogDrawer';
import useHotkeys from './hooks/useHotkeys';
import './crm.css';
import { ToastProvider } from './components/Toast';
import { crmBasePath } from './utils/crmEnv';

const CRM_THEME_STORAGE_KEY = 'crmThemePreference';

const CRM_SECTION_GUIDES = {
    dashboard: {
        label: 'Dashboard',
        title: 'Dashboard Guide',
        description: 'Use the dashboard to quickly track CRM performance and spot what needs attention.',
        steps: [
            'Check top summary cards for today/selected range performance.',
            'Review trend charts and pipeline summaries to spot drops or bottlenecks.',
            'Click into the relevant section (Leads, Deals, Activities) to take action.',
        ],
        tips: [
            'Use the dashboard for monitoring, then manage records in the section pages.',
            'For assigned team members, counts reflect records assigned to them.',
        ],
    },
    leads: {
        label: 'Leads',
        title: 'Leads Guide',
        description: 'Leads is your main working queue for incoming prospects.',
        steps: [
            'Use search and filters to find leads by status, source, owner, score, or tags.',
            'Open a lead to view full details, notes, timeline, and follow-up actions.',
            'Assign, update status, and create activities/deals as the lead progresses.',
        ],
        tips: [
            'Use the Default view to see all leads. "My Leads" only shows assigned leads.',
            'Bulk actions are useful for assignment and tagging.',
        ],
    },
    contacts: {
        label: 'Contacts',
        title: 'Contacts Guide',
        description: 'Contacts stores qualified people and relationship records linked to leads/deals.',
        steps: [
            'Search or filter contacts to locate a person/company record.',
            'Open a contact to review linked lead/deals and activity history.',
            'Update contact details and ownership as responsibilities change.',
        ],
        tips: [
            'Contacts are best for long-term relationship tracking after lead qualification.',
        ],
    },
    deals: {
        label: 'Deals',
        title: 'Deals Guide',
        description: 'Deals tracks pipeline opportunities from creation to close.',
        steps: [
            'Use pipeline/list views to review deals by stage and owner.',
            'Open a deal to update stage, value, notes, and next steps.',
            'Link or review associated lead/contact/property records.',
        ],
        tips: [
            'Keep stages updated so dashboard and pipeline reporting stay accurate.',
        ],
    },
    properties: {
        label: 'Properties',
        title: 'Properties Guide',
        description: 'Properties helps you manage inventory and match supply to demand.',
        steps: [
            'Add or review property records and listing details.',
            'Use filters to narrow by location, type, or status.',
            'Open a property to inspect matches and linked CRM entities.',
        ],
        tips: [
            'Keep property status current to improve match quality and team visibility.',
        ],
    },
    duplicates: {
        label: 'Duplicates',
        title: 'Duplicates Guide',
        description: 'Duplicates helps you identify records that may represent the same person or data.',
        steps: [
            'Review duplicate suggestions and compare records carefully.',
            'Choose the correct record to keep and merge/update details as needed.',
            'Recheck affected Leads/Contacts after resolving conflicts.',
        ],
        tips: [
            'Confirm phone/email before merging to avoid data loss.',
        ],
    },
    activities: {
        label: 'Activities',
        title: 'Activities Guide',
        description: 'Activities is your follow-up and task control center.',
        steps: [
            'View scheduled tasks/calls/meetings and sort by due date.',
            'Complete, reschedule, or log activity outcomes from the list.',
            'Open linked lead/contact/deal when you need more context.',
        ],
        tips: [
            'Keep overdue activities clean to maintain team accountability.',
        ],
    },
    'lead-sources': {
        label: 'Lead Sources',
        title: 'Lead Sources Guide',
        description: 'Lead Sources controls inbound lead integrations like webhooks, Google Sheets, and Meta.',
        steps: [
            'Click Connect Source to create a new integration.',
            'Choose routing mode: Fixed Owner (one person) or Round Robin (rotation across selected assignees).',
            'Use Configure/Run Sync/Pause/Delete buttons on each source to manage it safely.',
        ],
        tips: [
            'Test sources after setup to confirm leads are arriving and assignments are correct.',
            'Deleting a source stops new leads from that integration; existing leads remain in CRM.',
        ],
    },
    audit: {
        label: 'Audit Log',
        title: 'Audit Log Guide',
        description: 'Audit Log shows key system and user actions for traceability.',
        steps: [
            'Filter/search the log to find a specific action or record change.',
            'Review timestamps and actor details when troubleshooting.',
            'Use it to verify what changed and when.',
        ],
        tips: [
            'Audit logs are best for debugging operational issues or confirming edits.',
        ],
    },
    automations: {
        label: 'Automations',
        title: 'Automations Guide',
        description: 'Automations lets you create rule-based actions for CRM events.',
        steps: [
            'Review existing automations and their enabled/disabled state.',
            'Create or edit rules, triggers, and actions.',
            'Test safely before enabling in production workflows.',
        ],
        tips: [
            'Start with narrow conditions to avoid unintended bulk actions.',
        ],
    },
    team: {
        label: 'Team',
        title: 'Team Guide',
        description: 'Team is where the owner/admin manages CRM seats, invites, and access.',
        steps: [
            'Invite new members and assign the correct role (admin/manager/agent).',
            'Review active seats and remove/disable access if needed.',
            'Reset credentials or PINs for members when necessary.',
        ],
        tips: [
            'Use least-privilege access: agents for day-to-day lead handling, admin for settings.',
        ],
    },
    default: {
        label: 'CRM',
        title: 'CRM Guide',
        description: 'Use the CRM sections in the sidebar to manage leads, activities, deals, and operations.',
        steps: [
            'Choose a section from the sidebar.',
            'Use filters/search to narrow results.',
            'Open records and take actions from the detail pages.',
        ],
        tips: [
            'Use keyboard shortcut `Ctrl/Cmd + K` to open the command palette.',
        ],
    },
};

const resolveGuideForPath = (pathname, basePath) => {
    const normalizedPath = String(pathname || '');
    const normalizedBase = String(basePath || '').replace(/\/$/, '');
    const relative = normalizedPath.startsWith(normalizedBase)
        ? normalizedPath.slice(normalizedBase.length)
        : normalizedPath;
    const section = relative.split('/').filter(Boolean)[0] || 'dashboard';
    return CRM_SECTION_GUIDES[section] || CRM_SECTION_GUIDES.default;
};

const CRMLayout = ({ children }) => {
    const history = useHistory();
    const location = useLocation();
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);
    const crmTheme = 'light';
    const basePath = useMemo(() => crmBasePath(), [location.pathname]);
    const currentGuide = useMemo(
        () => resolveGuideForPath(location.pathname, basePath),
        [location.pathname, basePath],
    );
    const floatingButtonStyle = useMemo(
        () => ({
            pointerEvents: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            background: 'rgba(255,255,255,0.94)',
            color: 'var(--crm-gray-900)',
            border: '1px solid rgba(10,10,10,0.08)',
            backdropFilter: 'blur(6px)',
        }),
        [],
    );

    useEffect(() => {
        setGuideOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const body = document.body;
        const root = document.documentElement;
        const previousBodyDarkMode = body.classList.contains('dark-mode');
        const previousRootDarkMode = root.classList.contains('dark-mode');
        const previousCrmLightMode = body.classList.contains('crm-light-mode');

        return () => {
            body.classList.toggle('dark-mode', previousBodyDarkMode);
            root.classList.toggle('dark-mode', previousRootDarkMode);
            body.classList.toggle('crm-light-mode', previousCrmLightMode);
            if (!body.classList.contains('crm-light-mode')) {
                body.removeAttribute('data-crm-theme');
            }
        };
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return;

        const body = document.body;
        const root = document.documentElement;
        const isDark = crmTheme === 'dark';

        body.classList.toggle('dark-mode', isDark);
        root.classList.toggle('dark-mode', isDark);
        body.classList.toggle('crm-light-mode', !isDark);
        body.setAttribute('data-crm-theme', crmTheme);

        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.setItem(CRM_THEME_STORAGE_KEY, crmTheme);
            }
        } catch (error) {
            // Ignore storage write failures.
        }
    }, [crmTheme]);

    useHotkeys({
        'mod+k': () => setPaletteOpen((prev) => !prev),
        'esc': () => setPaletteOpen(false),
        'n l': () => window.dispatchEvent(new CustomEvent('crm:quickAddLead')),
        'n c': () => window.dispatchEvent(new CustomEvent('crm:quickAddContact')),
        'n d': () => window.dispatchEvent(new CustomEvent('crm:quickAddDeal')),
        'n a': () => window.dispatchEvent(new CustomEvent('crm:quickAddActivity')),
        'g d': () => history.push(`${basePath}`),
        'g l': () => history.push(`${basePath}/leads`),
        'g c': () => history.push(`${basePath}/contacts`),
        'g e': () => history.push(`${basePath}/deals`),
        'g a': () => history.push(`${basePath}/activities`),
        '/': () => {
            const input = document.querySelector('[data-crm-search]');
            if (input) input.focus();
        },
    });

    return (
        <ToastProvider>
            <div data-crm-app-shell style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--crm-bg-secondary)' }}>
                <CRMSidebar />
                <main className="crm-container" style={{ flex: 1, overflow: 'auto' }}>
                    <div
                        style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 20,
                            display: 'flex',
                            gap: 8,
                            justifyContent: 'flex-end',
                            padding: '12px 16px 0',
                            pointerEvents: 'none',
                        }}
                    >
                        <button
                            type="button"
                            className="crm-btn crm-btn-secondary crm-btn-sm"
                            onClick={() => setGuideOpen(true)}
                            aria-haspopup="dialog"
                            aria-expanded={guideOpen ? 'true' : 'false'}
                            style={floatingButtonStyle}
                        >
                            Guide: {currentGuide.label}
                        </button>
                    </div>
                    {children}
                </main>
            </div>
            {guideOpen && (
                <div className="crm-modal-overlay" onClick={() => setGuideOpen(false)}>
                    <div
                        className="crm-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label={currentGuide.title}
                        onClick={(event) => event.stopPropagation()}
                        style={{ maxWidth: 760, width: '100%' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">{currentGuide.title}</h2>
                            <button className="crm-modal-close" type="button" onClick={() => setGuideOpen(false)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <p className="crm-text-muted" style={{ marginTop: 0 }}>
                                {currentGuide.description}
                            </p>

                            <div className="crm-card" style={{ marginBottom: 12 }}>
                                <div className="crm-card-body">
                                    <h3 style={{ marginTop: 0, marginBottom: 8 }}>How to use</h3>
                                    <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                                        {currentGuide.steps.map((step) => (
                                            <li key={step} style={{ marginBottom: 6 }}>{step}</li>
                                        ))}
                                    </ol>
                                </div>
                            </div>

                            <div className="crm-card">
                                <div className="crm-card-body">
                                    <h3 style={{ marginTop: 0, marginBottom: 8 }}>Tips</h3>
                                    <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                                        {currentGuide.tips.map((tip) => (
                                            <li key={tip} style={{ marginBottom: 6 }}>{tip}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="crm-toolbar-actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
                                <button className="crm-btn crm-btn-primary" type="button" onClick={() => setGuideOpen(false)}>
                                    Got it
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
            <QuickLogDrawer />
        </ToastProvider>
    );
};

export default CRMLayout;
